import { DraftLanguage, SOCIAL_NETWORK_ORDER, SocialCampaignDTO, SocialNetwork, UpdateSocialCampaignSettingsDTO } from 'models/socialCampaign'
import { SocialCampaignTarget, useUpdateSocialCampaignSettings } from 'dataQueries/socialCampaign.query'
import { failurePropertiesOf, joinValues, trackSocialCampaignEvent } from 'utils/analytics/socialCampaign'
import { useCallback, useMemo, useRef, useState } from 'react'

import { AnalyticsEvent } from 'utils/analytics/AnalyticsEvent'

/**
 * The networks and language of the panel: shown as chosen at once, saved one change after the other.
 *
 * Each change is a PUT of what changed; a change made while one is on its way waits for it and is
 * merged with the next ones, so the backend always ends on the last choice and two saves never race.
 * A failure drops what was not saved yet and re-reads the campaign to show it as the backend has it
 * (a 409 means it changed elsewhere, e.g. ended in another tab).
 */
export function useCampaignSettings(
  target: SocialCampaignTarget,
  campaign: SocialCampaignDTO | null | undefined,
  refetchCampaign: () => Promise<unknown>
) {
  const { mutateAsync } = useUpdateSocialCampaignSettings()
  const [chosen, setChosen] = useState<UpdateSocialCampaignSettingsDTO | null>(null)
  const [failed, setFailed] = useState(false)
  const inFlight = useRef(false)
  const queued = useRef<UpdateSocialCampaignSettingsDTO | null>(null)
  // the campaign a refused change was sent from, for its analytics
  const campaignRef = useRef(campaign)
  campaignRef.current = campaign

  const networks = useMemo(() => chosen?.networks ?? campaign?.networks ?? [], [campaign?.networks, chosen?.networks])
  const language = chosen?.draftLanguage ?? campaign?.draftLanguage ?? DraftLanguage.EN

  const flush = useCallback(async () => {
    if (inFlight.current) return
    inFlight.current = true
    try {
      while (queued.current) {
        const body = queued.current
        queued.current = null
        const tracked = {
          changed: joinValues([body.networks && 'networks', body.draftLanguage && 'language'].filter((name): name is string => !!name)),
          networks: body.networks && joinValues(body.networks),
          networkCount: body.networks?.length,
          draftLanguage: body.draftLanguage,
        }
        let saved: SocialCampaignDTO
        try {
          saved = await mutateAsync({ ...target, body })
        } catch (failure) {
          // a refused write has no server event: this one is the only trace of it
          trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_SETTINGS_SAVED, target, campaignRef.current, { ...tracked, ...failurePropertiesOf(failure) })
          throw failure
        }
        trackSocialCampaignEvent(AnalyticsEvent.SOCIAL_CAMPAIGN_SETTINGS_SAVED, target, saved, { ...tracked, outcome: 'ok' })
      }
      setChosen(null)
    } catch {
      // the API layer reports the failure; the panel says it and shows what the backend has
      queued.current = null
      setChosen(null)
      setFailed(true)
      void refetchCampaign()
    } finally {
      inFlight.current = false
    }
  }, [mutateAsync, refetchCampaign, target])

  const change = useCallback((body: UpdateSocialCampaignSettingsDTO) => {
    setFailed(false)
    setChosen((current) => ({ ...current, ...body }))
    queued.current = { ...queued.current, ...body }
    void flush()
  }, [flush])

  const setNetwork = useCallback((network: SocialNetwork, selected: boolean) => {
    const next = SOCIAL_NETWORK_ORDER.filter((item) => item === network ? selected : networks.includes(item))
    if (!next.length) return
    change({ networks: next })
  }, [change, networks])

  const setLanguage = useCallback((next: DraftLanguage) => {
    if (next !== language) change({ draftLanguage: next })
  }, [change, language])

  const resetFailure = useCallback(() => setFailed(false), [])

  // a change is shown as chosen until its save is done, failed included: on its way or waiting for the one ahead
  const saving = chosen !== null

  return { networks, language, failed, saving, setNetwork, setLanguage, resetFailure }
}
